// @ai-generated 2026-08-11T06:18:46+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { useIdLookup } from '@Hooks/useIdLookup';

import { useToasts, useTranslate } from '@synqo/frontend-component-library';
import { useCallback, useEffect } from 'react';
import { useListApi } from '@Hooks/useListApi';
import { ContainerTypesRepository } from '@Repositories/Stammdaten';

/** What a reader needs from a container type that the action linking to it does not carry. */
export interface ContainerTypeDetails {
    /** Whether the type is tracked per individual container rather than counted as a pool. */
    usesIndividualManagement: boolean;
    /** The name the portal shows the type under, empty when the operator set none. */
    externalLabel: string;
}

/** The looked-up container types, by type id. */
export type ContainerTypeDetailsByTypeId = ReadonlyMap<string, ContainerTypeDetails>;

/**
 * Looks up how the container types the given ids name are managed, because an action carries only
 * the id and the label of its type.
 */
export function useContainerTypeDetails(
    containerTypeIds: readonly (string | undefined)[],
): ContainerTypeDetailsByTypeId {
    const i18n = useTranslate('');
    const { pushToast } = useToasts();
    const { loadBatch } = useListApi(ContainerTypesRepository, STAMMDATEN_API);

    const loadDetails = useCallback(
        async (containerTypeIdsToLoad: string[]) => {
            const loaded = await loadBatch({
                page_no: 0,
                page_size: containerTypeIdsToLoad.length,
                full_text_search: '',
                sort_columns: [],
                include_ids: containerTypeIdsToLoad,
                exclude_ids: [],
            });

            return loaded.items.map(
                (type) =>
                    [
                        type.id,
                        {
                            usesIndividualManagement: type.uses_individual_management,
                            externalLabel: type.external_label,
                        },
                    ] as const,
            );
        },
        [loadBatch],
    );

    // A type whose record never arrived reads as collective, so a failed call must not settle the
    // question for the whole frame: an individually managed type would be shown as a counted line
    // with a row-deleting stepper.
    const warnLoadFailed = useCallback(() => {
        pushToast({ message: i18n('Die Containertypen konnten nicht geladen werden.'), variant: 'warning' });
    }, [pushToast, i18n]);

    const { valuesById, request } = useIdLookup(loadDetails, warnLoadFailed);

    useEffect(() => request(containerTypeIds), [containerTypeIds, request]);

    return valuesById;
}
